/**
 * Players Page
 * Display all missing players with filtering and modal details
 */

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { PlayerCard } from '@/components/PlayerCard';
import { FilterBar } from '@/components/FilterBar';
import { PlayerModal } from '@/components/PlayerModal';
import { usePlayerStore } from '@/store/playerStore';
import { mockPlayers, getUniqueTeams } from '@/data/mockData';
import type { MissingPlayer } from '@/types';

export default function Players() {
  const { filters, setFilters, getFilteredPlayers, setPlayers, players } = usePlayerStore();
  const [selectedPlayer, setSelectedPlayer] = useState<MissingPlayer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use mock data for now (will be replaced with API data)
  useMemo(() => {
    if (players.length === 0) {
      setPlayers(mockPlayers);
    }
  }, [players.length, setPlayers]);

  const filteredPlayers = getFilteredPlayers();
  const uniqueTeams = useMemo(() => getUniqueTeams(players), [players]);

  const handlePlayerClick = (player: MissingPlayer) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedPlayer(null), 200);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
          离场选手
        </h1>
        <p className="text-secondary">
          2025 年活跃但 2026 年消失在赛场上的 LPL 选手
        </p>
      </motion.div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        teams={uniqueTeams}
      />

      {/* Results Count */}
      <div className="mb-6 text-tertiary text-sm">
        共 {filteredPlayers.length} 名选手
      </div>

      {/* Players Grid */}
      {filteredPlayers.length > 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {filteredPlayers.map((player, index) => (
            <motion.div
              key={player.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <PlayerCard player={player} onClick={handlePlayerClick} />
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <div className="text-center py-20">
          <p className="text-tertiary text-lg mb-4">没有找到匹配的选手</p>
          <button
            onClick={() => setFilters({ position: 'all', team: 'all', search: '' })}
            className="text-secondary hover:text-primary transition-colors"
          >
            清除筛选条件
          </button>
        </div>
      )}

      {/* Player Modal */}
      <PlayerModal
        player={selectedPlayer}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
